import React, { useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Button from 'components/Button/Button';
import {
  ErrorText,
  Heading,
  SecondaryText,
  SecondaryTitle
} from 'components/core/Typography';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import moment, { Moment } from 'moment';
import UploadFiles, { FileUpload } from './UploadFiles';
import { GraphError } from 'types/general';
import { commitMutation, graphql } from 'relay-runtime';
import environment from 'api/environment';
import { AutomatedCheckModalMutationResponse } from './__generated__/AutomatedCheckModalMutation.graphql';
import Info from './modal-sections/Info';
import Application from './modal-sections/Application';
import Reference from './modal-sections/Reference';
import DBS from './modal-sections/DBS';
import {
  AutomatedCheckModal_Run_Mutation,
  AutomatedCheckModal_Run_MutationResponse
} from './__generated__/AutomatedCheckModal_Run_Mutation.graphql';
import Interview from './modal-sections/Interview';
import ID from './modal-sections/ID';
import Progress from './modal-sections/Progress';
import { useToast } from 'components/Toasts/ToastProvider';
import CheckResult from './modal-sections/CheckResult';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: 500,
    padding: 25
  },
  modalTitle: {
    padding: '16px 22px',
    fontSize: 17,
    fontWeight: 700
  },
  select: {
    gap: '21px',
    margin: '5px 0px',
    display: 'flex',
    padding: 10,
    borderRadius: 8,
    '&:hover': {
      cursor: 'pointer',
      background: '#f6f7fa'
    }
  },
  selectID: {
    extend: 'select',
    flexDirection: 'column'
  },
  label: {
    fontSize: 15,
    fontWeight: 400
  },
  button: {
    width: 'calc(100% - 40px)',
    margin: 20,
    padding: 21
  },
  divider: {
    width: '100%',
    height: 1,
    background: '#a4a4a442',
    margin: '30px 0px'
  },
  selectionBoxes: {
    display: 'flex',
    gap: '20px',
    margin: '20px 0px'
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'flex-end'
  },
  dateInput: {
    border: '1px solid #e7e7e7',
    padding: 10,
    borderRadius: 6
  }
}));

const mutation = graphql`
  mutation AutomatedCheckModalMutation($delegateUUID: UUID!) {
    startVerify(delegateUUID: $delegateUUID)
  }
`;

const runCheckMutation = graphql`
  mutation AutomatedCheckModal_Run_Mutation(
    $verificationUUID: UUID!
    $idExpiry: Time
  ) {
    runAutoVerify(
      verificationUUID: $verificationUUID
      idExpiryDate: $idExpiry
    ) {
      success
      shouldSendToHuman
      application {
        success
        errors
      }
      reference {
        success
        errors
      }
      interview {
        success
        errors
      }
      ident: id {
        success
        errors
      }
      dbs {
        success
        errors
      }
    }
  }
`;

type Props = {
  userName: string;
  delegateUUID: string;
  onClose: () => void;
  successPennyPrice: number;
  failurePennyPrice: number;
};

export type SectionName =
  | 'info'
  | 'application'
  | 'reference'
  | 'interview'
  | 'id'
  | 'dbs'
  | 'result';

export default function AutomatedCheckModal({
  successPennyPrice,
  failurePennyPrice,
  delegateUUID,
  onClose
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const toast = useToast();

  const [verificationID, setVerificationID] = React.useState<
    string | undefined
  >();
  const [result, setResult] = React.useState<
    AutomatedCheckModal_Run_MutationResponse | undefined
  >(undefined);
  const [resultError, setResultError] = React.useState<string | undefined>();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [approvals, setApprovals] = React.useState<{
    idExpiry: undefined | Moment;
    idApproved: boolean;
  }>({
    idExpiry: undefined,
    idApproved: false
  });

  const sections: SectionName[] = [
    'info',
    'application',
    'reference',
    'interview',
    'id',
    'dbs',
    'result'
  ];

  const [sectionFiles, setSectionFiles] = React.useState<
    {
      [key in SectionName]: FileUpload[];
    }
  >({
    info: [],
    application: [],
    reference: [],
    interview: [],
    id: [],
    dbs: [],
    result: []
  });

  const [sectionName, setSectionName] = React.useState<SectionName>('info');

  const sectionIndex = sections.indexOf(sectionName);

  const startVerify = () => {
    commitMutation(environment, {
      mutation,
      variables: {
        delegateUUID: delegateUUID
      },
      onCompleted: async (
        response: AutomatedCheckModalMutationResponse,
        errors: GraphError[]
      ) => {
        if (errors) {
          // Display error
          console.log(`${errors[0]?.extensions?.message}`);
          return;
        }

        console.log('VerificationUUID', response.startVerify);
        setVerificationID(response.startVerify);
      },
      onError: (err: any) => console.error(err)
    });
  };

  const runCheck = () => {
    if (!approvals.idApproved) {
      toast({
        message: {
          variant: 'error',
          text: 'Please approve the ID document'
        }
      });
      return;
    }

    // if (!approvals.idExpiry) {
    //   toast({
    //     message: {
    //       variant: 'error',
    //       text: 'Please enter the ID expiry date'
    //     }
    //   });
    //   return;
    // }

    setLoading(true);
    commitMutation(environment, {
      mutation: runCheckMutation,
      variables: {
        verificationUUID: verificationID,
        idExpiry: approvals.idExpiry?.format()
      },
      onCompleted: async (
        response: AutomatedCheckModal_Run_MutationResponse,
        errors: GraphError[]
      ) => {
        setLoading(false);

        if (errors) {
          // Display error
          console.log(`${errors[0]?.extensions?.message}`);
          return;
        }

        setResult(response);
        console.log('VerificationUUID', response.runAutoVerify);
      },
      onError: (err: any) => {
        setLoading(false);
        setResultError(err);
        console.error(err);
      }
    });
  };

  const goNext = () => {
    if (sectionName == 'application' && sectionFiles.application.length == 0) {
      toast({
        message: {
          variant: 'error',
          text: 'Please upload an application form'
        }
      });
    }

    if (sectionName == 'reference' && sectionFiles.reference.length == 0) {
      toast({
        message: {
          variant: 'error',
          text: 'Please upload reference documents'
        }
      });
    }

    setSectionName(sections[sectionIndex + 1]);
  };

  return (
    <div
      className={classes.container}
      onClick={(evt) => {
        evt.stopPropagation();
      }}
    >
      <Progress selected={sectionName} />
      {sectionName == 'result' && (
        <CheckResult
          isLoading={isLoading}
          errorData={result}
          onNext={() => {
            onClose();
          }}
        />
      )}
      {sectionName == 'info' && (
        <Info
          successPennyPrice={successPennyPrice}
          failPennyPrice={failurePennyPrice}
          onNext={async () => {
            startVerify();
            goNext();
          }}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
        />
      )}
      {sectionName == 'application' && (
        <Application
          sectionName={sectionName}
          delegateUUID={delegateUUID}
          verificationID={verificationID}
          files={sectionFiles?.application}
          onUpdateFiles={(files) =>
            setSectionFiles((f) => ({ ...f, application: files }))
          }
          onNext={() => goNext()}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
        />
      )}
      {sectionName == 'interview' && (
        <Interview
          sectionName={sectionName}
          delegateUUID={delegateUUID}
          verificationID={verificationID}
          files={sectionFiles?.interview}
          onUpdateFiles={(files) =>
            setSectionFiles({ ...sectionFiles, interview: files })
          }
          onNext={() => {
            goNext();
          }}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
        />
      )}
      {sectionName == 'reference' && (
        <Reference
          sectionName={sectionName}
          delegateUUID={delegateUUID}
          verificationID={verificationID}
          files={sectionFiles?.reference}
          onUpdateFiles={(files) =>
            setSectionFiles((f) => ({ ...f, reference: files }))
          }
          onNext={() => goNext()}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
        />
      )}
      {sectionName == 'id' && (
        <ID
          sectionName={sectionName}
          delegateUUID={delegateUUID}
          verificationID={verificationID}
          files={sectionFiles?.id}
          onUpdateFiles={(files) =>
            setSectionFiles({ ...sectionFiles, id: files })
          }
          onNext={() => {
            goNext();
            // Submit everything for verification
          }}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
          idDate={approvals.idExpiry}
          onIDDate={(date) => setApprovals({ ...approvals, idExpiry: date })}
          idApproved={approvals.idApproved}
          onIDApproved={(approved) =>
            setApprovals({ ...approvals, idApproved: approved })
          }
        />
      )}
      {sectionName == 'dbs' && (
        <DBS
          sectionName={sectionName}
          delegateUUID={delegateUUID}
          verificationID={verificationID}
          files={sectionFiles?.dbs}
          onUpdateFiles={(files) =>
            setSectionFiles({ ...sectionFiles, dbs: files })
          }
          onNext={() => {
            runCheck();
            goNext();
            // Submit everything for verification
          }}
          onPrev={() => setSectionName(sections[sectionIndex - 1])}
          successPennyPrice={successPennyPrice}
        />
      )}
    </div>
  );
}
